import React, { useCallback, useMemo, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import Popup from '@atlaskit/popup';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { SourcesPopupContent } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/SourcesPopupContent';
import type { Sources as SourcesType } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	MAX_VISIBLE_LOGOS,
	SourceLogoStack,
} from '@atlassian/rovo-platform-ui-components/source-logo-stack';
import { getSourceType } from '@atlassian/rovo-platform-ui-components/source-logo-stack/source-util';

import type { SpaceSource } from '../create-space-modal/types';

const ANALYTICS_CHANNEL = 'aiMate';

const messages = defineMessages({
	overflowSourceCount: {
		id: 'rovo-spaces.recommended-spaces-sources-trigger.overflow-source-count',
		defaultMessage: '+{count} Sources',
		description:
			'Label shown next to source logos indicating how many additional sources are associated with a recommended space',
	},
	viewSourcesLabel: {
		id: 'rovo-spaces.recommended-spaces-sources-trigger.view-sources-label',
		defaultMessage: 'View {count, plural, one {# source} other {# sources}} for {spaceTitle}',
		description:
			'Accessible label for the trigger that opens sources associated with a recommended space',
	},
});

const styles = cssMap({
	triggerWrapper: {
		alignSelf: 'start',
	},
	pressable: {
		display: 'flex',
		minHeight: '32px',
		paddingBlockStart: token('space.0'),
		paddingBlockEnd: token('space.0'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		justifyContent: 'center',
		alignItems: 'center',
		gap: token('space.075'),
		borderRadius: token('radius.medium'),
		borderColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		backgroundColor: 'transparent',
		boxSizing: 'border-box',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	selectedPressable: {
		display: 'flex',
		minHeight: '32px',
		paddingBlockStart: token('space.0'),
		paddingBlockEnd: token('space.0'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		justifyContent: 'center',
		alignItems: 'center',
		gap: token('space.075'),
		borderRadius: token('radius.medium'),
		borderColor: token('color.border.selected'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		backgroundColor: token('color.background.selected.pressed'),
		boxSizing: 'border-box',
		cursor: 'pointer',
	},
});

export interface RecommendedSpacesSourcesTriggerProps {
	sources: SpaceSource[];
	sourceUrls: string[];
	spaceTitle: string;
	sourceCount?: number;
	testId?: string;
}

const mapSourcesForPopup = (sources: SpaceSource[]): SourcesType =>
	sources
		.filter((source) => source.url)
		.map((source) => ({
			ari: source.ari === source.url ? '' : source.ari,
			title: source.name ?? source.url,
			type: source.sourceType,
			url: source.url,
			lastModified: '',
			product: source.sourceType,
		}));

export const RecommendedSpacesSourcesTrigger = ({
	sources,
	sourceUrls,
	spaceTitle,
	sourceCount = sourceUrls.length,
	testId = 'recommended-spaces-sources-trigger',
}: RecommendedSpacesSourcesTriggerProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const popupSources = useMemo(() => mapSourcesForPopup(sources), [sources]);

	const fireTriggerAnalytics = useCallback(
		(state: 'open' | 'closed') => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'sources',
				attributes: {
					source: 'recommendedSpaces',
					state,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const handleSourceLinkClick = useCallback(
		(type: string, ari?: string, product?: string, index?: number) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'sourceLink',
				attributes: {
					index,
					is3pLink: !ari,
					product,
					source: 'recommendedSpaces',
					sourceType: type,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const handleClose = useCallback(() => {
		if (isOpen) {
			fireTriggerAnalytics('closed');
		}
		setIsOpen(false);
	}, [fireTriggerAnalytics, isOpen]);

	const handleToggle = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			const nextIsOpen = !isOpen;
			fireTriggerAnalytics(nextIsOpen ? 'open' : 'closed');
			setIsOpen(nextIsOpen);
		},
		[fireTriggerAnalytics, isOpen],
	);

	if (popupSources.length === 0 || sourceUrls.length === 0) {
		return null;
	}

	const visibleLogoCount = Math.min(new Set(sourceUrls.map(getSourceType)).size, MAX_VISIBLE_LOGOS);
	const sourceOverflowCount = Math.max(0, sourceCount - visibleLogoCount);

	return (
		<Box xcss={styles.triggerWrapper}>
			<Popup
				placement="bottom-start"
				isOpen={isOpen}
				onClose={handleClose}
				content={() => (
					<SourcesPopupContent
						sources={popupSources}
						onClickSourceHandler={handleSourceLinkClick}
						onClose={handleClose}
					/>
				)}
				trigger={(triggerProps) => (
					<Pressable
						{...triggerProps}
						xcss={isOpen ? styles.selectedPressable : styles.pressable}
						testId={testId}
						aria-expanded={isOpen}
						aria-haspopup="dialog"
						aria-label={formatMessage(messages.viewSourcesLabel, {
							count: sourceCount,
							spaceTitle,
						})}
						onClick={handleToggle}
					>
						<SourceLogoStack sources={sourceUrls} hideOverflowCount />
						{sourceOverflowCount > 0 && (
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.overflowSourceCount, {
									count: sourceOverflowCount,
								})}
							</Text>
						)}
					</Pressable>
				)}
			/>
		</Box>
	);
};
